import React from 'react';
import { Alert, Card, Row, Col } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useOwnerCommandCenter } from '@/hooks/useOwnerIntelligence';
import { useCurrency } from '@/contexts/CurrencyContext';
import {
  HeroKpiCard,
  PrimaryKpiCard,
  WorryAboutTodayPanel,
  InsightActionCard,
  ProportionalBar,
  OiPageFrame,
  OiMetricHelp,
} from '@/components/owner-intelligence/OwnerIntelligenceComponents';
import { StrategyContextStrip } from '@/components/owner-intelligence/StrategyContextStrip';
import { scoreToSignalTier, ragToSignalTier } from '@/components/owner-intelligence/oi-signals';
import type { RagStatus } from '@/services/ownerIntelligence.service';

const MONEY_COLORS = ['var(--oi-signal-blue)', 'var(--oi-amber)', 'var(--oi-crimson)'];
const EXPECTED_WIDGET_KEYS = ['revenue_mtd', 'receivables', 'cash_available', 'order_book'];

const OwnerIntelligenceHomePage: React.FC = () => {
  const { data, isLoading, error } = useOwnerCommandCenter();
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();

  const visibleKeys = new Set((data?.widgets ?? []).map((w) => w.key));
  const missingFinanceWidgets = EXPECTED_WIDGET_KEYS.filter((k) => !visibleKeys.has(k)).length >= 3;

  return (
    <OiPageFrame loading={isLoading && !data} error={Boolean(error || !data)} errorMessage="Could not load command center">
      {data ? (
        <>
          <StrategyContextStrip compact />
          {missingFinanceWidgets ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Limited view"
              description="Some KPIs are hidden because your role lacks finance or sales module access. Contact your administrator if you need a broader view."
            />
          ) : null}
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={10}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <HeroKpiCard
                  label="Operational Health"
                  value={`${data.business_health.composite_score}%`}
                  score={data.business_health.composite_score}
                  statusTier={scoreToSignalTier(data.business_health.composite_score)}
                  sublabel={`Updated ${new Date(data.refreshed_at).toLocaleString()}`}
                />
                <OiMetricHelp title="Operational health score">
                  Weighted score from open exceptions across finance, delivery, manufacturing, procurement, inventory, and quality.
                  It reflects operational risk — not profit or cash balance.
                </OiMetricHelp>
              </div>
            </Col>
            <Col xs={24} lg={14}>
              <Card title="Executive KPIs" className="oi-widget-grid" bordered={false}>
                <Row gutter={[12, 12]}>
                  {data.widgets
                    .filter((w) => w.key !== 'business_health')
                    .slice(0, 8)
                    .map((w) => (
                      <Col xs={12} sm={8} md={6} key={w.key}>
                        <PrimaryKpiCard
                          label={w.label}
                          value={
                            w.unit === 'currency'
                              ? formatCurrency(Number(w.value))
                              : w.unit === '%'
                                ? `${w.value}%`
                                : String(w.value)
                          }
                          deltaPct={w.delta_pct}
                          trend={w.trend}
                          sparkline={w.sparkline}
                          accentTier={w.status ? ragToSignalTier(w.status as RagStatus) : undefined}
                          onClick={w.link ? () => navigate(w.link!) : undefined}
                        />
                      </Col>
                    ))}
                </Row>
              </Card>
            </Col>
          </Row>

          {data.worry_about_today.some((w) => w.severity === 'critical') ? (
            <div className="oi-alert-strip">
              {data.worry_about_today
                .filter((w) => w.severity === 'critical')
                .map((w, i) => (
                  <div key={i} className="oi-alert-strip__item">
                    {w.message}
                  </div>
                ))}
            </div>
          ) : null}

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} lg={12}>
              <Card title="Requires attention today" bordered={false}>
                <WorryAboutTodayPanel
                  items={data.worry_about_today}
                  onItemAction={(link) => navigate(link)}
                />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Where is money stuck?" bordered={false}>
                {data.money_stuck.length === 0 ? (
                  <span className="oi-text-muted">No significant trapped value detected.</span>
                ) : (
                  <>
                    <ProportionalBar
                      segments={data.money_stuck.map((m, i) => ({
                        label: m.category,
                        amount: m.amount,
                        color: MONEY_COLORS[i % MONEY_COLORS.length],
                      }))}
                    />
                    {data.money_stuck.map((m, i) => (
                      <div key={i} className="oi-money-row" style={{ marginTop: 12 }}>
                        <strong className="oi-mono-value">{formatCurrency(m.amount)}</strong>
                        <span className="oi-money-row__label">{m.category}</span>
                        {m.note ? <div className="oi-money-row__note">{m.note}</div> : null}
                      </div>
                    ))}
                  </>
                )}
              </Card>
            </Col>
          </Row>

          <Card title="Recommended actions" style={{ marginTop: 16 }} bordered={false}>
            <Row gutter={[16, 16]}>
              {data.business_health.recommended_actions.map((a, i) => (
                <Col xs={24} md={12} lg={8} key={i}>
                  <InsightActionCard
                    title={a.title}
                    description={a.description}
                    priority={a.priority}
                    onAction={a.linked_route ? () => navigate(a.linked_route!) : undefined}
                  />
                </Col>
              ))}
            </Row>
          </Card>

        </>
      ) : null}
    </OiPageFrame>
  );
};

export default OwnerIntelligenceHomePage;
